import { createServerFn } from '@tanstack/react-start'
import {
  getRequestHeader,
  setCookie,
  deleteCookie,
} from '@tanstack/react-start/server'
import { createAdminClient, createSessionClient } from '../lib/appwrite'
import { redirect } from '@tanstack/react-router'
import { OAuthProvider } from 'node-appwrite'

export const authMiddleware = createServerFn({ method: 'GET' }).handler(
  async () => {
    const cookieHeader = getRequestHeader('cookie')
    if (!cookieHeader) return { currentUser: null }

    const cookies = cookieHeader.split(';').reduce(
      (acc, current) => {
        const [key, value] = current.trim().split('=')
        acc[key] = value
        return acc
      },
      {} as Record<string, string>,
    )

    const sessionKey = Object.keys(cookies).find((key) =>
      key.startsWith('a_session_'),
    )
    const session = sessionKey ? cookies[sessionKey] : undefined

    if (!session) return { currentUser: null }

    try {
      const { account } = await createSessionClient(session)
      const user = await account.get()
      return { currentUser: user }
    } catch {
      return { currentUser: null }
    }
  },
)

export const signInWithGoogleFn = createServerFn({ method: 'GET' })
  .inputValidator((data: { redirect?: string }) => data)
  .handler(async ({ data }) => {
    const { account } = createAdminClient()
    const origin =
      getRequestHeader('origin') ||
      process.env.APP_URL ||
      'http://localhost:3000'

    // Build the success URL with the redirect parameter included
    const successUrl = new URL(`${origin}/callback`)
    if (data.redirect) {
      successUrl.searchParams.set('redirect', data.redirect || '/dashboard')
    }

    // Create OAuth2 session
    const redirectUrl = await account.createOAuth2Token(
      OAuthProvider.Google,
      successUrl.toString(),
      `${origin}/auth/sign-in?error=oauth_failed`,
    )

    throw redirect({ href: redirectUrl })
  },
  )

export const handleOAuthCallbackFn = createServerFn({ method: 'GET' })
  .inputValidator(
    (data: { userId: string; secret: string; redirect?: string, redirectToPayment?: string, productId?: string }) => data,
  )
  .handler(async ({ data }) => {
    const { userId, secret, redirect: inputRedirect, redirectToPayment, productId } = data
    const { account } = createAdminClient()

    const session = await account.createSession(userId, secret)

    if (redirectToPayment) {
      throw redirect({ href: `/callback?redirectToPayment=true&productId=${productId}&userId=${userId}&secret=${secret}` })
    }

    const projectId = process.env.APPWRITE_PROJECT_ID
    setCookie(`a_session_${projectId}`, session.secret, {
      path: '/',
      httpOnly: true,
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
      maxAge: 60 * 60 * 24 * 30, // 30 days
    })

    // 1. Prioritize redirect from URL parameter (Pass-through state)
    // 2. Fallback to cookie (Old method)
    // 3. Absolute fallback to /dashboard
    const redirectTarget = inputRedirect || '/dashboard'
    if (redirectTarget.startsWith('/')) {
      throw redirect({ to: redirectTarget })
    } else {
      throw redirect({ href: redirectTarget })
    }
  })

export const signOutFn = createServerFn({ method: 'POST' }).handler(
  async () => {
    const projectId = process.env.APPWRITE_PROJECT_ID
    deleteCookie(`a_session_${projectId}`)
    throw redirect({ to: '/' })
  },
)

export const signInFn = createServerFn({ method: 'POST' })
  .inputValidator((data: { email: string, password: string, redirect?: string }) => data)
  .handler(async ({ data }) => {
    const { email, password } = data
    const { account } = createAdminClient()

    const session = await account.createEmailPasswordSession(email, password)

    const projectId = process.env.APPWRITE_PROJECT_ID
    setCookie(`a_session_${projectId}`, session.secret, {
      path: '/',
      httpOnly: true,
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
      maxAge: 60 * 60 * 24 * 30,
    })

    throw redirect({ to: data.redirect || '/dashboard' })
  })
